// ============================================================
// TMR Portal Admin — paginação client-side reutilizável
// ============================================================
import { useEffect, useState } from 'react';
import { Icons, StrokeIcon } from '../lib/icons';

/** Fatia uma lista em páginas. Reseta para a última página válida quando a lista encolhe. */
export function usePagination<T>(items: T[], pageSize: number) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);
  const current = Math.min(page, pageCount);
  const pageItems = items.slice((current - 1) * pageSize, current * pageSize);
  return { page: current, setPage, pageCount, pageItems, total: items.length };
}

function windowPages(page: number, pageCount: number): (number | '…')[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const out: (number | '…')[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  if (start > 2) out.push('…');
  for (let i = start; i <= end; i++) out.push(i);
  if (end < pageCount - 1) out.push('…');
  out.push(pageCount);
  return out;
}

export function Pagination({ page, pageCount, total, onChange }: { page: number; pageCount: number; total?: number; onChange: (p: number) => void }) {
  if (pageCount <= 1) return null;
  const pages = windowPages(page, pageCount);

  const arrow = (dir: 'prev' | 'next') => {
    const disabled = dir === 'prev' ? page <= 1 : page >= pageCount;
    return (
      <button
        type="button"
        className="pa-icon-btn"
        disabled={disabled}
        onClick={() => onChange(dir === 'prev' ? page - 1 : page + 1)}
        title={dir === 'prev' ? 'Anterior' : 'Próxima'}
        style={{ width: 32, height: 32, opacity: disabled ? 0.4 : 1, cursor: disabled ? 'default' : 'pointer' }}
      >
        <StrokeIcon d={dir === 'prev' ? Icons.chevronL : Icons.chevronR} size={15} />
      </button>
    );
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', paddingTop: 4 }}>
      {total != null ? <span style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>{total} {total === 1 ? 'registro' : 'registros'}</span> : <span />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {arrow('prev')}
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`e${i}`} style={{ padding: '0 4px', color: 'var(--fg-subtle)', fontSize: 13 }}>
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onChange(p)}
              style={{
                minWidth: 32,
                height: 32,
                padding: '0 8px',
                borderRadius: 'var(--radius-sm)',
                border: `1px solid ${p === page ? 'var(--forest-900)' : 'var(--border-default)'}`,
                background: p === page ? 'var(--forest-900)' : 'var(--cream-50)',
                color: p === page ? 'var(--cream-50)' : 'var(--fg-muted)',
                fontFamily: 'var(--font-mono)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {p}
            </button>
          ),
        )}
        {arrow('next')}
      </div>
    </div>
  );
}
